import { Component, inject, Injectable } from "@angular/core";
import { FormsModule, NgForm } from "@angular/forms";
import { finalize, Observable, of } from "rxjs";
import { CurrentUserService } from "../../../shared/CurrentUser.service";
import { AppRoles } from "../../../shared/enums/AppRoles";
import { FormControlHelper } from "../../../shared/helpers/FormControlHelper";
import { ChangeDetectionService } from "../../../shared/services/change-detection.service";
import { LoadingService } from "../../../shared/services/loading-overlay/loading-overlay.component";
import { VendorDetailModalService } from "../../vendor-detail-modal/vendor-detail-modal.service";
import { InvoiceModalService } from "../invoice-modal.service";
import { Invoice, InvoiceStatus } from "../types/Invoice";
import { InvoiceCreateDropdowns } from "../types/InvoiceCreateDropdowns";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatButtonModule } from "@angular/material/button";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { YesNoPipe } from "../../../shared/pipes/yes-no.pipe";
import { ScrollInsetShadowDirective } from "../../../directives/scroll-inset-shadow.directive";

interface ShowInvoiceCreateModalData {
  VendorId: number;
}

@Component({
  selector: 'invoice-create-modal',
  templateUrl: 'invoice-create-modal.component.html',
  styleUrl: 'invoice-create-modal.component.less',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatButtonToggleModule, MatSelectModule, MatOptionModule, MatFormFieldModule, ScrollInsetShadowDirective],
  providers: [LoadingService, ChangeDetectionService, VendorDetailModalService, InvoiceModalService, YesNoPipe]
})
export class InvoiceCreateModalComponent {
  private formControlHelper = inject(FormControlHelper)
  private loadingService = inject(LoadingService);
  private changeDetectionService = inject(ChangeDetectionService);
  private invoiceModalService = inject(InvoiceModalService)
  private userService = inject(CurrentUserService);
  private vendorService = inject(VendorDetailModalService);
  private dialogRef = inject<MatDialogRef<InvoiceCreateModalComponent, number>>(MatDialogRef);
  private showModalData = inject<ShowInvoiceCreateModalData>(MAT_DIALOG_DATA);

  private IsNewVendor: boolean = false;
  private UserIsAdmin: boolean = false;
  private UserRegionalAdminCountries: number[] = [];
  private QuotePurchaseOrderEndDates: Array<Date> = null;
  private VendorId: number = this.showModalData.VendorId;

  protected Dropdowns = {
    Years: <Array<number>>[],
    PoNumbers: <Array<string>>[],
    PrNumbers: <Array<string>>[],
    PoNumbersByYear: <{ [key: number]: Array<string> }>{},
    PrNumbersByYear: <{ [key: number]: Array<string> }>{}
  }
  protected Year: number;
  protected PurchaseOrderNumber: string;
  protected PurchaseRequisitionNumber: string;
  protected CreateCriteria: "PO" | "NOPO" = "PO";


  ngOnInit() {
    const loadRef = this.loadingService.StartWaiting("Fetching data");
    this.invoiceModalService.GetCreateDropdowns(this.showModalData.VendorId).subscribe((dropdowns) => {
      if (dropdowns) this.SetDropdowns(dropdowns);

      this.changeDetectionService.SetOriginalValue(this.GetChangeDetectionData());

      this.userService.HasRole(AppRoles.PaymentAdmin).subscribe((hasRole) => {
        this.UserIsAdmin = this.UserIsAdmin || hasRole;
        this.vendorService.GetVendor(this.showModalData.VendorId).subscribe((vendor) => {
          this.userService.GetPaymentAdminCountries().subscribe((countries) => {
            this.UserRegionalAdminCountries = countries;

            const isRegionalAdmin = this.IsNewVendor
              ? this.UserRegionalAdminCountries.length > 0
              : this.UserRegionalAdminCountries.find((c) => c == vendor.CountryId) != null;

            this.UserIsAdmin = this.UserIsAdmin || isRegionalAdmin;
          });
        });
      });

      loadRef.Stop();
    });
  }

  private SetDropdowns(dropdowns: InvoiceCreateDropdowns) {
    this.Dropdowns.Years = dropdowns.PurchaseOrdersAndRequisitions.map((a) => a.Year).sort();

    dropdowns.PurchaseOrdersAndRequisitions.forEach((tmp) => {
      this.Dropdowns.PoNumbersByYear[tmp.Year] = tmp.PurchaseOrderNumbers;
      this.Dropdowns.PrNumbersByYear[tmp.Year] = tmp.PurchaseRequisitionNumbers;
    });
  }

  protected OnYearChange() {
    this.Dropdowns.PoNumbers = this.Dropdowns.PoNumbersByYear[this.Year];
    this.Dropdowns.PrNumbers = this.Dropdowns.PrNumbersByYear[this.Year];
  }

  public SaveInvoice(form: NgForm) {
    if (!this.formControlHelper.CheckFormIsValid(form)) return;

    const loadingRef = this.loadingService.StartWaiting("Creating new Invoice");
    this.invoiceModalService.CreateInvoice(this.VendorId, {
      DocumentIds: [],
      InvoiceStatus: InvoiceStatus.New,
      PaymentCheck: null,
      PaymentDate: null,
      PurchaseOrderNumber: this.CreateCriteria == "PO" ? this.PurchaseOrderNumber : "",
      PurchaseRequisitionNumber: this.PurchaseRequisitionNumber ?? "",
      QuotePurchaseOrderEndDates: this.QuotePurchaseOrderEndDates,
      VendorAssignedInvoiceNumber: null,
      Year: this.Year
    })
      .pipe(finalize(() => loadingRef.Stop()))
      .subscribe((result: Invoice) => {
        this.dialogRef.close(result.Id);
      });
  }

  protected CancelModal() {
    this.CheckForChangesAndPrompt().subscribe((shouldLeave) => {
      if (!shouldLeave) return;
      this.dialogRef.close(undefined);
    });
  }

  private CheckForChangesAndPrompt(): Observable<boolean> {
    if (this.changeDetectionService.HasChanges(this.GetChangeDetectionData())) {
      return this.changeDetectionService.HasChangeDialog();
    }
    return of(true);
  }

  private GetChangeDetectionData = () => ({
    Year: this.Year,
    PONumber: this.PurchaseOrderNumber,
    PRNumber: this.PurchaseRequisitionNumber,
    CreateCriteria: this.CreateCriteria
  })
}

@Injectable()
export class InvoiceCreateModal {
  private dialog = inject(MatDialog);

  /** Displays the modal. Returns the id when the user closes the modal, or undefined if no record was created
   * @returns {Observable<number | undefined>} The ID or undefined if no record was created.
   */
  public ShowModal(data: ShowInvoiceCreateModalData): Observable<number | undefined> {
    const ref = this.dialog.open(InvoiceCreateModalComponent, {
      data: data,
      width: "400px",
      disableClose: true,
      autoFocus: false,
      panelClass: "invoice-create-modal-panel",
    });

    return ref.afterClosed();
  }
}
